import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, FolderOpen, Users, FileSpreadsheet, MoreVertical, Calendar, Clock, TrendingUp, AlertCircle, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { projectsApi } from "@/services/projects";
import { useDeleteProject, useProjects, projectsKeys } from "@/hooks/useProjects";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { Project } from "@/types/domain";
import { Search } from "lucide-react";

const statusConfig = {
  active: { label: 'Active', color: 'bg-info/10 text-info border-info/20' },
  inactive: { label: 'Inactive', color: 'bg-muted text-muted-foreground border-muted' },
};

const ProjectsList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'active' | 'inactive'>('active');
  const [deleteProject, setDeleteProject] = useState<Project | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Fetch projects from API — backend filters by status and search, no client-side re-filter
  const { data, isLoading, error, refetch } = useProjects({
    status: activeTab,
    search: debouncedSearch.trim() || undefined,
  });

  // Mutation for toggling project status
  const toggleStatusMutation = useMutation({
    mutationFn: ({ projectId, newStatus }: { projectId: string; newStatus: 'active' | 'inactive' }) =>
      projectsApi.toggleStatus(projectId, newStatus),
    onMutate: async ({ projectId, newStatus }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: projectsKeys.all });

      // Snapshot previous value for the current list variant
      const currentFilters = {
        status: activeTab,
        search: debouncedSearch.trim() || undefined,
      };
      const previousProjects = queryClient.getQueryData(projectsKeys.list(currentFilters));

      // Optimistically update the current list variant
      queryClient.setQueryData(projectsKeys.list(currentFilters), (old: { projects: Project[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          projects: old.projects.map((p: Project) =>
            p.id === projectId ? { ...p, status: newStatus } : p
          ),
        };
      });

      return { previousProjects, filters: currentFilters };
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousProjects && context.filters) {
        queryClient.setQueryData(projectsKeys.list(context.filters), context.previousProjects);
      }
      toast.error('Failed to update project status', error.message);
    },
    onSuccess: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: projectsKeys.all });
      toast.success('Project status updated successfully');
    },
  });

  const toggleProjectStatus = (projectId: string, newStatus: 'active' | 'inactive') => {
    toggleStatusMutation.mutate({ projectId, newStatus });
  };

  const deleteMutation = useDeleteProject();

  const getDaysLeft = (endDate?: string) => {
    if (!endDate) return null;
    const today = new Date();
    const deadlineDate = new Date(endDate);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 75) return 'bg-success';
    if (percentage >= 50) return 'bg-info';
    if (percentage >= 25) return 'bg-warning';
    return 'bg-destructive';
  };

  const projects = data?.projects || [];

  const ProjectCard = ({ project }: { project: Project }) => {
    const percentage = project.stats.progress_percentage ||
      Math.round((project.stats.completed_steps / project.stats.total_steps) * 100) || 0;
    const daysLeft = getDaysLeft(project.end_date);
    const isOverdue = daysLeft !== null && daysLeft < 0;

    return (
      <Card 
        className="card-hover cursor-pointer group"
        onClick={() => navigate(`/admin/projects/${project.id}/report`)}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <Badge 
              variant="outline" 
              className={statusConfig[project.status].color}
            >
              {statusConfig[project.status].label}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover border shadow-lg z-50">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/admin/projects/${project.id}/edit`); }}>
                  Edit Project
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/admin/projects/${project.id}/report`); }}>
                  View Report
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/admin/projects/${project.id}/subscriptions`); }}>
                  View Subscriptions
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {project.status !== 'inactive' ? (
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); toggleProjectStatus(project.id, 'inactive'); }}
                    className="text-warning"
                  >
                    Set Inactive
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={(e) => { e.stopPropagation(); toggleProjectStatus(project.id, 'active'); }}
                    className="text-success"
                  >
                    Set Active
                  </DropdownMenuItem>
                )}
                {project.status === 'inactive' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); setDeleteProject(project); setConfirmText(''); }}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Project
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <CardTitle className="text-lg font-display mt-2">{project.project_number} - {project.name}</CardTitle>
          <CardDescription className="line-clamp-2">
            {project.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress Gauge */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-semibold">{percentage}%</span>
            </div>
            <div className="relative">
              <Progress value={percentage} className="h-3" />
              <div 
                className={cn("absolute inset-0 h-3 rounded-full transition-all", getProgressColor(percentage))}
                style={{ width: `${percentage}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{project.stats.completed_steps} / {project.stats.total_steps} steps</span>
              {daysLeft !== null && (
                <div className={cn(
                  "flex items-center gap-1",
                  isOverdue ? "text-destructive" : daysLeft <= 7 ? "text-warning" : ""
                )}>
                  <Clock className="h-3 w-3" />
                  {isOverdue ? (
                    <span>{Math.abs(daysLeft)} days overdue</span>
                  ) : (
                    <span>{daysLeft} days left</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-2 rounded-lg bg-muted/50">
              <FileSpreadsheet className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-sm font-semibold">{project.stats.scenario_count}</p>
              <p className="text-xs text-muted-foreground">Scenarios</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-muted/50">
              <TrendingUp className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-sm font-semibold">{project.stats.total_steps}</p>
              <p className="text-xs text-muted-foreground">Steps</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-muted/50">
              <Users className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
              <p className="text-sm font-semibold">{project.ict_responsible}</p>
              <p className="text-xs text-muted-foreground">Owner</p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Created {new Date(project.created_at).toLocaleDateString()}
            </div>
            {project.end_date && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Due {new Date(project.end_date).toLocaleDateString()}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader title="Administration" />

      <main className="flex-1 px-6 py-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground">
                Projects
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage your test projects and configurations
              </p>
            </div>
            <Button
              size="lg"
              onClick={() => navigate("/admin/projects/new")}
              className="gap-2"
            >
              <Plus className="h-5 w-5" />
              New Project
            </Button>
          </div>

          {/* Search + Tabs */}
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search by name or project number..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'active' | 'inactive')}>
            <TabsList>
              <TabsTrigger value="active" className="gap-2">
                Active
                <Badge variant="secondary" className="ml-1">
                  {activeTab === 'active' ? projects.length : '—'}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="inactive" className="gap-2">
                Inactive
                <Badge variant="secondary" className="ml-1">
                  {activeTab === 'inactive' ? projects.length : '—'}
                </Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-6">
              {isLoading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <Card key={i}>
                      <CardHeader className="pb-3">
                        <Skeleton className="h-5 w-20 mb-2" />
                        <Skeleton className="h-6 w-3/4 mb-2" />
                        <Skeleton className="h-4 w-full" />
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Skeleton className="h-3 w-full" />
                          <Skeleton className="h-3 w-full" />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <Skeleton className="h-16 w-full" />
                          <Skeleton className="h-16 w-full" />
                          <Skeleton className="h-16 w-full" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : error ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error Loading Projects</AlertTitle>
                  <AlertDescription className="flex items-center justify-between">
                    <span>{error instanceof Error ? error.message : 'Failed to fetch projects'}</span>
                    <Button variant="outline" size="sm" onClick={() => refetch()}>
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : projects.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-16">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                      <FolderOpen className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">
                      {debouncedSearch.trim()
                        ? `No ${activeTab} projects matching "${debouncedSearch.trim()}"`
                        : activeTab === 'active' ? 'No active projects' : 'No inactive projects'}
                    </h3>
                    <p className="text-muted-foreground text-center mb-6 max-w-md">
                      {debouncedSearch.trim()
                        ? 'Try a different search term or clear the search to see all projects.'
                        : activeTab === 'active'
                          ? "Get started by creating your first project. You'll be guided through setting up your test scenarios."
                          : "Projects you set as inactive will appear here."}
                    </p>
                    {activeTab === 'active' && !debouncedSearch.trim() && (
                      <Button onClick={() => navigate("/admin/projects/new")} className="gap-2">
                        <Plus className="h-4 w-4" />
                        Create First Project
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {projects.map((project) => (
                    <ProjectCard key={project.id} project={project} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <AlertDialog open={!!deleteProject} onOpenChange={(open) => { if (!open) { setDeleteProject(null); setConfirmText(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project Permanently?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  This will permanently delete <strong>{deleteProject?.project_number} - {deleteProject?.name}</strong> and all associated data:
                </p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>{deleteProject?.stats.scenario_count || 0} scenarios and {deleteProject?.stats.total_steps || 0} steps</li>
                  <li>All test cases, issues, and user mappings</li>
                  <li>All activity logs and sync history</li>
                </ul>
                <p className="text-destructive font-medium">This action cannot be undone.</p>
                <div className="pt-2">
                  <label className="text-sm font-medium">
                    Type <strong>{deleteProject?.name}</strong> to confirm:
                  </label>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={deleteProject?.name}
                    className="mt-1"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => { setDeleteProject(null); setConfirmText(''); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={confirmText.trim() !== deleteProject?.name?.trim() || deleteMutation.isPending}
              onClick={() => {
                if (!deleteProject) return;
                toast.promise(
                  deleteMutation.mutateAsync(deleteProject.id),
                  {
                    loading: 'Deleting project...',
                    success: 'Project deleted successfully',
                    error: (err: Error) => `Failed to delete: ${err.message}`,
                  }
                );
                setDeleteProject(null);
                setConfirmText('');
              }}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <footer className="w-full py-4 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Lotus Bakeries. Internal Testing Portal.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ProjectsList;